/* eslint-disable no-param-reassign */
// param-reassign is possibe as redux tool kit is using immer internally
import { createSlice } from '@reduxjs/toolkit';
import { VALIDATION_MSG_TYPES } from 'utils/const';

const initialState = {
  selectedFiles: [], // used to find the last validation message and also for creating the s3 reports file name
  openValidationModal: false,
  fileUploadsInProgress: [], // array of file names
  uploadedFiles: [], // array of file names
  uploadErrorMsgByFileName: {},
  fileValidationsInProgress: [], // array of file names
  validationProgressPercent: 0,
  validatedFiles: [], // array of file names
  validFiles: [], // array of file names, criteria: can have warnings, but no errors
  validationMsgsByFileName: {},
  validationSummaryExpandedFiles: [], // array of file names
  isCancellingChanges: false,
  isSavingChanges: false,
};

const pplValidationSlice = createSlice({
  name: 'pplFileValidation',
  initialState,
  reducers: {
    // file upload and validation actions
    uploadInitiated: (state, action) => {
      state.selectedFiles = action.payload;
      state.openValidationModal = true;
      state.fileUploadsInProgress = [];
      state.uploadedFiles = [];
      state.uploadErrorMsgByFileName = {};
      state.fileValidationsInProgress = [];
      state.validationProgressPercent = 0;
      state.validatedFiles = [];
      state.validFiles = [];
      state.validationMsgsByFileName = {};
      state.validationSummaryExpandedFiles = [];
      // reset file version history states if file upload is trigerred from file version history modal
      state.openFileVersionHistory = false;
      state.pageNumber = 1;
      state.hasReachedFirstVersion = false;
      state.allFileVersions = [];
      state.openDeleteVersionConfirmationModal = false;
    },
    uploadInProgress: (state, action) => {
      if (!state.fileUploadsInProgress.includes(action.payload)) {
        state.fileUploadsInProgress.push(action.payload);
      }
    },
    uploadedSuccessfully: (state, action) => {
      state.fileValidationsInProgress.push(action.payload);
      state.fileUploadsInProgress = state.fileUploadsInProgress.filter(
        (str) => str !== action.payload,
      );
      state.uploadedFiles.push(action.payload);
    },
    uploadErrored: (state, action) => {
      state.uploadErrorMsgByFileName[action.payload.fileName] =
        action.payload.errorMsg;
      state.fileUploadsInProgress = state.fileUploadsInProgress.filter(
        (str) => str !== action.payload.fileName,
      );
    },
    uploadCancelledByFileName: (state, action) => {
      state.fileUploadsInProgress = state.fileUploadsInProgress.filter(
        (str) => str !== action.payload,
      );
      state.fileValidationsInProgress = state.fileValidationsInProgress.filter(
        (str) => str !== action.payload,
      );
    },
    uploadCancelledAll: (state) => {
      state.selectedFiles = [];
      state.fileUploadsInProgress = [];
      state.fileValidationsInProgress = [];
    },
    validationInProgress: (state, action) => {
      state.validationProgressPercent = action.payload;
    },
    addToValidationMsgs: (state, action) => {
      const newValidationMsgs = action.payload;

      newValidationMsgs.forEach((msg) => {
        if (state.validationMsgsByFileName[msg.fileName]) {
          state.validationMsgsByFileName[msg.fileName].push(msg);
        } else {
          state.validationMsgsByFileName[msg.fileName] = [msg];
        }
      });
    },
    reachedLastValidationMsg: (state, action) => {
      const fileName = action.payload;
      const hasErrors = state.validationMsgsByFileName[fileName]?.some(
        (msg) => msg.type === VALIDATION_MSG_TYPES.ERROR,
      );
      if (!hasErrors) {
        state.validFiles.push(fileName);
      }
      state.fileValidationsInProgress = state.fileValidationsInProgress.filter(
        (str) => str !== fileName,
      );
      state.validatedFiles.push(fileName);
    },
    toggleExpandedValidationSummary: (state, action) => {
      if (state.validationSummaryExpandedFiles.includes(action.payload)) {
        state.validationSummaryExpandedFiles =
          state.validationSummaryExpandedFiles.filter(
            (str) => str !== action.payload,
          );
      } else {
        state.validationSummaryExpandedFiles.push(action.payload);
      }
    },
    cancelledChanges: (state) => {
      state.isCancellingChanges = true;
    },
    cancelledValidation: (state) => {
      state.isCancellingChanges = false;
      state.fileValidationsInProgress = [];
      state.validationProgressPercent = 0;
      state.openValidationModal = false;
      state.validationProgressPercent = 0;
    },
    savingChanges: (state) => {
      state.isSavingChanges = true;
    },
    savedChanges: (state) => {
      state.isSavingChanges = false;
      state.openValidationModal = false;
      state.validationProgressPercent = 0; // timeout
    },
  },
});

export default pplValidationSlice.reducer;
export const {
  uploadInitiated,
  uploadInProgress,
  uploadedSuccessfully,
  uploadErrored,
  uploadCancelledByFileName,
  uploadCancelledAll,
  validationInProgress,
  addToValidationMsgs,
  reachedLastValidationMsg,
  cancelledValidation,
  toggleExpandedValidationSummary,
  cancelledChanges,
  savingChanges,
  savedChanges,
} = pplValidationSlice.actions;
